import { DateField } from './base';

/**
 * Staff engagement: welcome email sequences + feedback/NPS surveys.
 *
 * HARD RULE: these features target STAFF ONLY — account owners, admins, and
 * supervisors. Parents and children are NEVER enrolled or contacted. The
 * `StaffAudience` type and the enrollment code enforce this structurally.
 */

/** The only roles engagement features may ever contact. */
export type StaffAudience = 'owner' | 'admin' | 'supervisor';

export const STAFF_AUDIENCES: StaffAudience[] = ['owner', 'admin', 'supervisor'];

// ============================================================================
// WELCOME EMAIL SEQUENCES
// ============================================================================

/** A single step in a welcome sequence. */
export interface SequenceStep {
  /** Stable key for the step (used in logs + idempotency). */
  key: string;
  /** Days after enrollment this step should send. 0 = immediately. */
  dayOffset: number;
  subject: string;
}

/** Definition of a multi-step welcome sequence for one audience. */
export interface SequenceDefinition {
  id: string;
  audience: StaffAudience[];
  steps: SequenceStep[];
}

export type EnrollmentStatus = 'active' | 'completed' | 'canceled';

/**
 * One staff member's progress through a welcome sequence.
 * Stored at `emailSequenceEnrollments/{enrollmentId}`.
 */
export interface SequenceEnrollment {
  id: string;
  sequenceId: string;
  /** Auth user id of the enrolled staff member. */
  userId: string;
  accountId: string;
  /** Verified staff role at enrollment time — must be a StaffAudience. */
  role: StaffAudience;
  email: string;
  firstName?: string;
  lastName?: string;
  /** Keys of steps already sent (idempotency). */
  sentStepKeys: string[];
  status: EnrollmentStatus;
  enrolledAt: DateField;
  updatedAt?: DateField;
}

// ============================================================================
// SURVEYS / NPS
// ============================================================================

export type SurveyType = 'nps' | 'feedback';

/** Where a survey came from: a manual admin send, or the automated NPS sequence. */
export type SurveySource = 'admin' | 'sequence';

/** A survey campaign — either admin-sent or an automated NPS send. */
export interface Survey {
  id: string;
  type: SurveyType;
  title: string;
  /** Prompt shown to the respondent (e.g. the NPS question). */
  question: string;
  /** Audience is always staff — parents/children are never eligible. */
  audience: StaffAudience[];
  /** null = platform-wide (super-admin); otherwise scoped to one account. */
  accountId: string | null;
  status: 'draft' | 'sending' | 'sent' | 'closed';
  /** 'admin' for manual sends; 'sequence' for automated NPS. Defaults to 'admin'. */
  source?: SurveySource;
  createdBy: string;
  createdAt: DateField;
  sentAt?: DateField;
  /** Denormalized counts for the dashboard. */
  invitedCount?: number;
  responseCount?: number;
}

/**
 * A staff member's recurring-NPS enrollment. One per user. The processor sends
 * an NPS email whenever `nextDueAt` has elapsed, then reschedules it by the
 * cadence. Stored at `npsEnrollments/{userId}`.
 */
export interface NpsEnrollment {
  id: string;
  userId: string;
  accountId: string;
  role: StaffAudience;
  email: string;
  firstName?: string;
  lastName?: string;
  /** When the next NPS email should go out. */
  nextDueAt: DateField;
  /** Days between NPS sends (e.g. 90). */
  cadenceDays: number;
  /** Number of NPS emails sent so far. */
  sendCount: number;
  status: EnrollmentStatus;
  enrolledAt: DateField;
  lastSentAt?: DateField;
  updatedAt?: DateField;
}

/**
 * A single-use token that lets a staff member score an NPS email with one
 * click, no login. Stored at `npsTokens/{token}`. Records which survey +
 * user it maps to and whether it's been used.
 */
export interface NpsToken {
  id: string;
  surveyId: string;
  userId: string;
  accountId: string;
  role: StaffAudience;
  used: boolean;
  createdAt: DateField;
  usedAt?: DateField;
  /** The score submitted, once used (for the follow-up comment page). */
  score?: number;
}

/**
 * One staff member's response. Stored at `surveys/{surveyId}/responses/{responseId}`.
 * NPS score is 0–10; feedback surveys may omit it.
 */
export interface SurveyResponse {
  id: string;
  surveyId: string;
  userId: string;
  accountId: string;
  role: StaffAudience;
  /** 0–10 for NPS. */
  score?: number;
  comment?: string;
  createdAt: DateField;
}

/** NPS bucketing from a 0–10 score. */
export function npsCategory(score: number): 'detractor' | 'passive' | 'promoter' {
  if (score >= 9) return 'promoter';
  if (score >= 7) return 'passive';
  return 'detractor';
}

/** NPS = %promoters − %detractors, on -100..100. Returns null for no responses. */
export function computeNps(scores: number[]): number | null {
  if (!scores.length) return null;
  let promoters = 0;
  let detractors = 0;
  for (const s of scores) {
    const c = npsCategory(s);
    if (c === 'promoter') promoters++;
    else if (c === 'detractor') detractors++;
  }
  return Math.round(((promoters - detractors) / scores.length) * 100);
}
